import React, {
    ButtonHTMLAttributes,
    forwardRef,
    ReactNode,
    useId,
} from 'react';

import { twMerge } from 'tailwind-merge';
import BaseButton from './BaseButton';
import Tooltip, { OriginalPlacements } from './Tooltip';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
    tooltipLabel?: string;
    tooltipPlacement?: OriginalPlacements;
    dataTestId?: string;
    className?: string;
}

// Whether the button shows text of its own. An icon child is an element and
// aria-hidden; a string or number child is what the user reads on the button.
const hasVisibleText = (children: ReactNode) =>
    React.Children.toArray(children).some(
        (child) => typeof child === 'string' || typeof child === 'number',
    );

// The ref reaches the <button>, for a caller that moves focus to it.
const IconButton = forwardRef<HTMLButtonElement, Props>(function IconButton(
    {
        className,
        onClick,
        children,
        tooltipLabel,
        tooltipPlacement,
        dataTestId,
        ...props
    },
    ref,
) {
    const descriptionId = useId();

    if (!tooltipLabel) {
        return (
            <BaseButton
                ref={ref}
                onClick={onClick}
                className={twMerge('hover:darken-10 border-none', className)}
                data-test-id={dataTestId}
                {...props}
            >
                {children}
            </BaseButton>
        );
    }

    // The tooltip is a hover affordance: its text lives in a portal that only
    // mounts on mouseenter, so it can never name the button for a keyboard or
    // screen-reader user. An icon-only button holds nothing but an
    // (aria-hidden) icon, so without an aria-label it announces as a bare
    // "button". A button with visible text already has its name, and the
    // tooltip is a reason rather than a name: giving it aria-label would make
    // a button that reads "Ask to join" announce as "Waiting for host" (WCAG
    // 2.5.3, Label in Name). That text becomes the description instead,
    // carried by a visually hidden span so it is there whether or not the
    // tooltip is open and even when the button is disabled and cannot be
    // hovered. The span sits next to the button, not inside it: a button is
    // named from its contents, hidden or not, so inside it would still be
    // read as part of the name. An explicit aria-label from the call site
    // still wins.
    const labelledByText = hasVisibleText(children);

    return (
        <>
            <Tooltip label={tooltipLabel} placement={tooltipPlacement}>
                <BaseButton
                    ref={ref}
                    onClick={onClick}
                    className={twMerge(
                        'hover:darken-10 border-none',
                        className,
                    )}
                    data-test-id={dataTestId}
                    aria-label={labelledByText ? undefined : tooltipLabel}
                    aria-describedby={
                        labelledByText ? descriptionId : undefined
                    }
                    {...props}
                >
                    {children}
                </BaseButton>
            </Tooltip>
            {labelledByText && (
                <span id={descriptionId} className="sr-only">
                    {tooltipLabel}
                </span>
            )}
        </>
    );
});

export default IconButton;
